import { Icons } from "assets";
import classNames from "classnames";
import { useEffect, useId, useState, useRef } from "react";
import { getLeftPosition, getTopPosition } from "shared/utils/helper";
import OptionsDropDown from "../options";
import styles from "./style.module.scss";

interface OptionProps {
  value: string;
  title: string;
  icon?: string;
}

// @ts-ignore
interface InputProps extends React.HTMLProps<HTMLInputElement> {
  label?: string;
  error?: string;
  warning?: string;
  notRequiredMargin?: boolean;
  customLabelStyle?: string;
  customInputContainer?: string;
  customOptionsContainer?: string;
  customIconStyle?: string;
  readOnlyColor?: string;
  options: OptionProps[];
  direction?: "top" | "bottom";
  wrapInGlobalWrapper?: boolean;
  onChange?: (selectedOption: OptionProps) => void;
}

const CustomSelect = ({
  label,
  required,
  error,
  warning,
  value,
  placeholder,
  notRequiredMargin,
  customLabelStyle,
  customInputContainer,
  customOptionsContainer,
  customIconStyle,
  readOnly,
  readOnlyColor,
  options,
  tabIndex,
  direction = "top",
  wrapInGlobalWrapper = true,
  onChange,
}: InputProps) => {
  const [openProfileDropDown, setOpenDropDown] = useState<boolean>(false);
  const uid = useId();
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [selectedOption, setSelectedOption] = useState<string | null>(
    // @ts-ignore
    value || null
  );

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setOpenDropDown(false);
      }
    };

    const handleScroll = () => {
      setOpenDropDown(false);
    };

    if (openProfileDropDown) {
      document.addEventListener("mousedown", handleClickOutside);
      window.addEventListener("scroll", handleScroll, true);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      window.removeEventListener("scroll", handleScroll, true);
    };
  }, [openProfileDropDown]);

  useEffect(() => {
    if (openProfileDropDown && wrapInGlobalWrapper) {
      const elem = document.getElementById(
        `custom-select-container-${uid}`
      ) as HTMLDivElement;
      const optionsDropDown: HTMLDivElement | null = document.getElementById(
        `custom-select-dropdown-${uid}`
      ) as HTMLDivElement;
      if (elem && optionsDropDown) {
        const topPosition = getTopPosition(elem);
        const leftPosition = getLeftPosition(elem);
        if (direction === "top") {
          optionsDropDown.style.top = `${topPosition + elem.clientHeight}px`;
        } else {
          optionsDropDown.style.top = `${
            topPosition - optionsDropDown.clientHeight + 20
          }px`;
        }
        optionsDropDown.style.left = `${leftPosition}px`;
        optionsDropDown.style.width = `${elem.clientWidth}px`;
      }
    }
  }, [openProfileDropDown, direction, uid, wrapInGlobalWrapper]);

  const handleOptionSelect = (option: OptionProps) => {
    setSelectedOption(option.value);
    setOpenDropDown(false);
    if (onChange) onChange(option);
  };

  return (
    <div
      className={classNames(
        "relative w-full",
        notRequiredMargin ? "" : error ? "mb-0" : "mb-3"
      )}
      id={`custom-select-container-${uid}`}
      ref={dropdownRef}
    >
      <div className="flex flex-col items-start w-full gap-2">
        {label && (
          <label className={classNames(styles.inputLabel, customLabelStyle)}>
            {label} {required && <span className={styles.asterik}>*</span>}
          </label>
        )}

        <div
          className={classNames(
            styles.inputContainer,
            readOnly ? styles.readonly : "",
            customInputContainer,
            "w-full gap-2 cursor-pointer"
          )}
          style={readOnlyColor ? { background: readOnlyColor } : {}}
          onClick={() => {
            if (!readOnly) setOpenDropDown((prev) => !prev);
          }}
        >
          <span
            className={classNames(
              selectedOption ? styles.value : styles.placeholder
            )}
          >
            {selectedOption || placeholder}
          </span>
          <Icons.ChevDown
            className={classNames(customIconStyle, styles.iconStyle)}
          />
        </div>

        {openProfileDropDown && (
          <OptionsDropDown
            id={`custom-select-dropdown-${uid}`}
            options={options}
            openSelection={openProfileDropDown}
            setOpenSelection={setOpenDropDown}
            onSelect={handleOptionSelect}
            customContainer={classNames(
              styles.optionsContainer,
              customOptionsContainer
            )}
            tabIndex={tabIndex}
            wrapInGlobalWrapper={wrapInGlobalWrapper}
          />
        )}
      </div>
      {error && <div className={classNames(styles.error)}>{error}</div>}
      {warning && <div className={classNames(styles.warning)}>{warning}</div>}
    </div>
  );
};

export default CustomSelect;
